import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes } from '@angular/router';
import { catchError, map, of, take, timeout } from 'rxjs';
import { ConfigService } from './services/config.service';
import { AuthService } from './services/auth.service';
import { canAccessSystemMonitorGuard } from './trading-hub/system-monitor/system-monitor.guard';
import { canAccessBoAnnouncementsGuard } from './trading-hub/bo-announcements/bo-announcements.guard';
import { configReadyGuard } from './services/config-ready.guard';

export const landingPageGuard: CanActivateFn = () => {
  const router = inject(Router);
  const tradingHub = router.createUrlTree(['/trading-hub']);
  return inject(ConfigService)
    .getConfig()
    .pipe(
      take(1),
      map((cfg) =>
        // Fail-closed: only show the landing page when the backend explicitly says it is enabled.
        // A missing landing_page flag (older backend, or one that omits it from public-config)
        // must NOT be treated as enabled - that silently ignored FEATURE_FLAG_LANDING_PAGE=none.
        (cfg?.feature_flags?.landing_page ?? 'none') === 'none' ? tradingHub : true,
      ),
      // If config is slow or fails we cannot confirm the landing page is enabled, so redirect to
      // /trading-hub rather than show it. /trading-hub is a real page (login), not a blank screen.
      timeout({ first: 4000, with: () => of(tradingHub) }),
      catchError(() => of(tradingHub)),
    );
};

// Redirects to the home page when the retail feature is disabled
// (FEATURE_FLAG_RETAIL / retail_service === 'none'). Mirrors landingPageGuard: never blocks
// the app on a slow/failed config fetch - defaults to allowing through so an enabled
// deployment is not bounced by a slow public-config load.
export const retailEnabledGuard: CanActivateFn = () => {
  const router = inject(Router);
  return inject(ConfigService)
    .getConfig()
    .pipe(
      take(1),
      map((cfg) =>
        (cfg?.feature_flags?.retail_service ?? 'none') === 'none'
          ? router.createUrlTree(['/'])
          : true,
      ),
      timeout({ first: 4000, with: () => of(true) }),
      catchError(() => of(true)),
    );
};

// P0-6: Redirects unauthenticated users to the login page before the dashboard
// component can throw and leave the user on a blank protected page.
const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (!authService.isLoggedIn()) {
    return router.createUrlTree(['/trading-hub']);
  }
  return true;
};

// Redirects to /trading-hub/dashboard when the active org team has no KYC module access.
const orgKycGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const orgTeam = authService.activeOrgTeam();
  if (!orgTeam) return true; // user data not loaded yet — let the component handle it
  if (orgTeam.isSuperAdmin) return true;
  const kycRole = (orgTeam.assignments ?? []).find(a => a.module === 'kyc')?.role;
  if (!kycRole || kycRole === 'none') return router.createUrlTree(['/trading-hub/dashboard']);
  return true;
};

// P1-2: Redirects org users to the KYC page when KYC is not yet verified.
// BO members bypass this check. If user data is not loaded yet, the component
// is responsible for handling the unverified state.
const kycRequiredGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const user = authService.currentUser();
  if (!user) return true; // data not loaded yet
  if (user.backoffice?.isMember) return true; // BO members bypass KYC requirement
  const orgTeam = authService.activeOrgTeam();
  if (!orgTeam) return true;
  if (orgTeam.isSuperAdmin && orgTeam.orgKycStatus !== 'verified') {
    return router.createUrlTree(['/trading-hub/dashboard/kyc']);
  }
  return true;
};

export const routes: Routes = [
  {
    path: '',
    title: 'Home',
    canActivate: [landingPageGuard],
    loadComponent: () => import('./home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: 'landing-page',
    title: 'Home',
    canActivate: [landingPageGuard],
    loadComponent: () => import('./home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: 'remittance',
    canActivate: [retailEnabledGuard],
    loadChildren: () => import('./retail/retail.routes').then((m) => m.retailRoutes),
  },
  {
    path: 'trading-hub',
    title: 'Login',
    loadComponent: () => import('./trading-hub/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'trading-hub/keycloak-callback',
    loadComponent: () =>
      import('./trading-hub/keycloak-callback/keycloak-callback.component').then(
        (m) => m.KeycloakCallbackComponent,
      ),
  },
  {
    path: 'trading-hub/keycloak-reauth-callback',
    loadComponent: () =>
      import('./trading-hub/keycloak-reauth-callback/keycloak-reauth-callback.component').then(
        (m) => m.KeycloakReauthCallbackComponent,
      ),
  },
  {
    path: 'trading-hub/privacy-policy',
    title: 'Privacy Policy',
    loadComponent: () =>
      import('./trading-hub/legal-doc-page.component').then((m) => m.LegalDocPageComponent),
    data: { docType: 'privacy-policy', title: 'Privacy Policy' },
  },
  {
    path: 'trading-hub/terms-of-use',
    title: 'Terms of Use',
    loadComponent: () =>
      import('./trading-hub/legal-doc-page.component').then((m) => m.LegalDocPageComponent),
    data: { docType: 'terms-of-use', title: 'Terms of Use' },
  },
  {
    path: 'trading-hub/dashboard',
    canActivate: [authGuard, configReadyGuard],
    loadComponent: () =>
      import('./trading-hub/dashboard.component').then((m) => m.DashboardComponent),
    children: [
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      {
        path: 'overview',
        title: 'Dashboard',
        loadComponent: () =>
          import('./trading-hub/dashboard/dashboard.component').then(
            (m) => m.DashboardPageComponent,
          ),
      },
      {
        path: 'wallet',
        title: 'Wallet',
        loadComponent: () =>
          import('./trading-hub/wallet/wallet.component').then((m) => m.WalletPageComponent),
      },
      {
        path: 'easy-convert',
        title: 'Exchange',
        canActivate: [kycRequiredGuard],
        loadComponent: () =>
          import('./trading-hub/easy-convert/easy-convert.component').then(
            (m) => m.EasyConvertPageComponent,
          ),
      },
      {
        path: 'fx-convert',
        title: 'OTC Convert',
        canActivate: [kycRequiredGuard],
        loadComponent: () =>
          import('./trading-hub/fx-convert/fx-convert.component').then(
            (m) => m.FxConvertPageComponent,
          ),
      },
      {
        path: 'retail',
        title: 'Market Making',
        loadComponent: () =>
          import('./trading-hub/retail/retail.component').then((m) => m.RetailPageComponent),
      },
      {
        path: 'wholesale',
        title: 'Indicative Offers',
        loadComponent: () =>
          import('./trading-hub/wholesale/wholesale.component').then(
            (m) => m.WholesalePageComponent,
          ),
      },
      {
        path: 'settlements',
        title: 'Settlements',
        loadComponent: () =>
          import('./trading-hub/settlements/settlements.component').then(
            (m) => m.SettlementsPageComponent,
          ),
      },
      {
        path: 'team',
        title: 'Team',
        loadComponent: () =>
          import('./trading-hub/team/team.component').then((m) => m.TeamPageComponent),
      },
      {
        path: 'bo-organisations',
        title: 'Organisations',
        loadComponent: () =>
          import('./trading-hub/organisations/organisations.component').then(
            (m) => m.OrganisationsPageComponent,
          ),
      },
      {
        path: 'bo-payment-gateways',
        title: 'Payment Gateways',
        loadComponent: () =>
          import('./trading-hub/payment-gateways/payment-gateways.component').then(
            (m) => m.PaymentGatewaysPageComponent,
          ),
      },
      {
        path: 'bo-backoffice-team',
        title: 'Backoffice Team',
        loadComponent: () =>
          import('./trading-hub/backoffice-team/backoffice-team.component').then(
            (m) => m.BackofficeTeamPageComponent,
          ),
      },
      {
        path: 'bo-currency-settings',
        title: 'Currency Settings',
        loadComponent: () =>
          import('./trading-hub/currency-settings/currency-settings.component').then(
            (m) => m.CurrencySettingsPageComponent,
          ),
      },
      {
        path: 'bo-custom-fees',
        title: 'Custom Fees',
        loadComponent: () =>
          import('./trading-hub/custom-fees/custom-fees.component').then(
            (m) => m.CustomFeesPageComponent,
          ),
      },
      {
        path: 'bo-payment-methods',
        title: 'Payment Methods',
        loadComponent: () =>
          import('./trading-hub/payment-methods/payment-methods.component').then(
            (m) => m.PaymentMethodsPageComponent,
          ),
      },
      {
        path: 'org-payment-methods',
        title: 'Payment Methods',
        loadComponent: () =>
          import('./trading-hub/org-payment-methods/org-payment-methods.component').then(
            (m) => m.OrgPaymentMethodsPageComponent,
          ),
      },
      {
        path: 'bo-markets',
        title: 'Markets',
        loadComponent: () =>
          import('./trading-hub/markets/markets.component').then((m) => m.MarketsPageComponent),
      },
      {
        path: 'bo-audit-trails',
        title: 'Audit Trails',
        loadComponent: () =>
          import('./trading-hub/audit-trails/audit-trails.component').then(
            (m) => m.AuditTrailsPageComponent,
          ),
      },
      {
        path: 'bo-process-codes',
        title: 'Process Codes',
        loadComponent: () =>
          import('./trading-hub/process-codes/process-codes.component').then(
            (m) => m.ProcessCodesPageComponent,
          ),
      },
      {
        path: 'bo-fx-convert',
        title: 'OTC Convert',
        loadComponent: () =>
          import('./trading-hub/bo-fx-convert/bo-fx-convert.component').then(
            (m) => m.BoFxConvertComponent,
          ),
      },
      {
        path: 'setup-passkey',
        title: 'Set Up Passkey',
        loadComponent: () =>
          import('./trading-hub/setup-passkey/setup-passkey.component').then(
            (m) => m.SetupPasskeyComponent,
          ),
      },
      {
        path: 'profile',
        title: 'Your Account',
        loadComponent: () =>
          import('./trading-hub/profile/profile.component').then((m) => m.ProfileComponent),
      },
      {
        path: 'org-api-keys',
        title: 'API Keys',
        loadComponent: () =>
          import('./trading-hub/org-api-keys/org-api-keys.component').then(
            (m) => m.OrgApiKeysPageComponent,
          ),
      },
      {
        path: 'org-deposits',
        title: 'Deposits',
        canActivate: [kycRequiredGuard],
        loadComponent: () =>
          import('./trading-hub/org-deposits/org-deposits.component').then(
            (m) => m.OrgDepositsPageComponent,
          ),
      },
      {
        path: 'org-withdrawals',
        title: 'Withdrawals',
        canActivate: [kycRequiredGuard],
        loadComponent: () =>
          import('./trading-hub/org-withdrawals/org-withdrawals.component').then(
            (m) => m.OrgWithdrawalsPageComponent,
          ),
      },
      {
        path: 'org-transactions',
        title: 'Transactions',
        loadComponent: () =>
          import('./trading-hub/org-transactions/org-transactions.component').then(
            (m) => m.OrgTransactionsPageComponent,
          ),
      },
      {
        path: 'bo-overview',
        title: 'Backoffice Overview',
        loadComponent: () =>
          import('./trading-hub/bo-overview/bo-overview.component').then(
            (m) => m.BoOverviewPageComponent,
          ),
      },
      {
        path: 'bo-deposits',
        title: 'BO Deposits',
        loadComponent: () =>
          import('./trading-hub/bo-deposits/bo-deposits.component').then(
            (m) => m.BoDepositsPageComponent,
          ),
      },
      {
        path: 'bo-withdrawals',
        title: 'BO Withdrawals',
        loadComponent: () =>
          import('./trading-hub/bo-withdrawals/bo-withdrawals.component').then(
            (m) => m.BoWithdrawalsPageComponent,
          ),
      },
      {
        path: 'bo-transactions',
        title: 'All Transactions',
        loadComponent: () =>
          import('./trading-hub/bo-transactions/bo-transactions.component').then(
            (m) => m.BoTransactionsPageComponent,
          ),
      },
      {
        path: 'bo-remittance',
        title: 'Retail Remittance',
        loadComponent: () =>
          import('./trading-hub/bo-remittance/bo-remittance.component').then(
            (m) => m.BoRemittanceComponent,
          ),
      },
      {
        path: 'bo-remittance-revenue-share',
        title: 'Remittance Revenue Share',
        loadComponent: () =>
          import('./trading-hub/bo-remittance-revenue-share/bo-remittance-revenue-share.component').then(
            (m) => m.BoRemittanceRevenueShareComponent,
          ),
      },
      {
        path: 'bo-deposit-exception',
        title: 'Deposit Exceptions',
        loadComponent: () =>
          import('./trading-hub/deposit-exception/deposit-exception.component').then(
            (m) => m.DepositExceptionPageComponent,
          ),
      },
      {
        path: 'bo-withdrawal-exception',
        title: 'Withdrawal Exceptions',
        loadComponent: () =>
          import('./trading-hub/withdrawal-exception/withdrawal-exception.component').then(
            (m) => m.WithdrawalExceptionPageComponent,
          ),
      },
      {
        path: 'bo-trades',
        title: 'All Trades',
        loadComponent: () =>
          import('./trading-hub/bo-trades/bo-trades.component').then((m) => m.BoTradesComponent),
      },
      {
        path: 'bo-revenue-shares',
        title: 'Revenue Share',
        loadComponent: () =>
          import('./trading-hub/bo-revenue-shares/bo-revenue-shares.component').then(
            (m) => m.BoRevenueSharesComponent,
          ),
      },
      {
        path: 'bo-indicative-settlements',
        title: 'Indicative Settlements',
        loadComponent: () =>
          import('./trading-hub/bo-indicative-settlements/bo-indicative-settlements.component').then(
            (m) => m.BoIndicativeSettlementsComponent,
          ),
      },
      {
        path: 'bo-manual-settlement',
        title: 'Manual Settlement',
        loadComponent: () =>
          import('./trading-hub/manual-settlement/manual-settlement.component').then(
            (m) => m.ManualSettlementComponent,
          ),
      },
      {
        path: 'bo-gateway-reconciliation',
        title: 'Gateway Reconciliation',
        loadComponent: () =>
          import('./trading-hub/bo-gateway-reconciliation/bo-gateway-reconciliation.component').then(
            (m) => m.BoGatewayReconciliationComponent,
          ),
      },
      {
        path: 'org-trades',
        title: 'My Trades',
        canActivate: [kycRequiredGuard],
        loadComponent: () =>
          import('./trading-hub/org-trades/org-trades.component').then((m) => m.OrgTradesComponent),
      },
      {
        path: 'org-revenue-shares',
        title: 'Revenue Share',
        loadComponent: () =>
          import('./trading-hub/org-revenue-shares/org-revenue-shares.component').then(
            (m) => m.OrgRevenueSharesComponent,
          ),
      },
      {
        path: 'bo-management-reports',
        title: 'Management Report',
        loadComponent: () =>
          import('./trading-hub/management-reports/management-reports.component').then(
            (m) => m.ManagementReportsComponent,
          ),
      },
      {
        path: 'bo-exchange',
        title: 'Exchange',
        loadComponent: () =>
          import('./trading-hub/bo-exchange/bo-exchange.component').then(
            (m) => m.BoExchangeComponent,
          ),
      },
      {
        path: 'bo-market-making',
        title: 'Market Making',
        loadComponent: () =>
          import('./trading-hub/bo-market-making/bo-market-making.component').then(
            (m) => m.BoMarketMakingComponent,
          ),
      },
      {
        path: 'bo-indicative-offers',
        title: 'Indicative Offers',
        loadComponent: () =>
          import('./trading-hub/bo-indicative-offers/bo-indicative-offers.component').then(
            (m) => m.BoIndicativeOffersComponent,
          ),
      },
      {
        path: 'bo-api-keys',
        title: 'API Keys',
        loadComponent: () =>
          import('./trading-hub/bo-api-keys/bo-api-keys.component').then(
            (m) => m.BoApiKeysComponent,
          ),
      },
      {
        path: 'kyc',
        title: 'KYC Verification',
        canActivate: [orgKycGuard],
        loadComponent: () =>
          import('./trading-hub/kyc/kyc.component').then((m) => m.KycPageComponent),
      },
      {
        path: 'bo-kyc',
        title: 'KYC Management',
        loadComponent: () =>
          import('./trading-hub/bo-kyc/bo-kyc.component').then((m) => m.BoKycPageComponent),
      },
      {
        path: 'bo-kyc-requirements',
        title: 'KYC Requirements',
        loadComponent: () =>
          import('./trading-hub/bo-kyc-requirements/bo-kyc-requirements.component').then(
            (m) => m.BoKycRequirementsComponent,
          ),
      },
      {
        path: 'bo-financial-institutions',
        title: 'Financial Institutions',
        loadComponent: () =>
          import('./trading-hub/bo-financial-institutions/bo-financial-institutions.component').then(
            (m) => m.BoFinancialInstitutionsComponent,
          ),
      },
      {
        path: 'org-docs',
        title: 'API Documentation',
        loadComponent: () =>
          import('./trading-hub/org-docs/org-docs.component').then((m) => m.OrgDocsPageComponent),
      },
      {
        path: 'bo-org-docs',
        title: 'API Documentation',
        loadComponent: () =>
          import('./trading-hub/org-docs/org-docs.component').then((m) => m.OrgDocsPageComponent),
      },
      {
        path: 'bo-system-monitor',
        title: 'System Monitor',
        canActivate: [canAccessSystemMonitorGuard],
        loadComponent: () =>
          import('./trading-hub/system-monitor/system-monitor.component').then(
            (m) => m.SystemMonitorComponent,
          ),
      },
      {
        path: 'bo-download-center',
        title: 'Download Center',
        loadComponent: () =>
          import('./trading-hub/bo-download-center/bo-download-center.component').then(
            (m) => m.BoDownloadCenterComponent,
          ),
      },
      {
        path: 'download-center',
        title: 'Download Center',
        loadComponent: () =>
          import('./trading-hub/download-center/download-center.component').then(
            (m) => m.DownloadCenterComponent,
          ),
      },
      {
        path: 'announcements',
        title: 'Announcements',
        loadComponent: () =>
          import('./trading-hub/announcements/announcements.component').then(
            (m) => m.AnnouncementsComponent,
          ),
      },
      {
        path: 'bo-announcements',
        title: 'Announcements',
        canActivate: [canAccessBoAnnouncementsGuard],
        loadComponent: () =>
          import('./trading-hub/bo-announcements/bo-announcements.component').then(
            (m) => m.BoAnnouncementsComponent,
          ),
      },
      {
        path: 'bo-broadcasts',
        title: 'Broadcasts',
        loadComponent: () =>
          import('./trading-hub/broadcasts/bo-broadcasts.component').then(
            (m) => m.BoBroadcastsComponent,
          ),
      },
      {
        path: 'inbox',
        title: 'Inbox',
        loadComponent: () =>
          import('./trading-hub/broadcasts/inbox.component').then((m) => m.InboxComponent),
      },
      {
        path: 'my-referrals',
        title: 'My Referrals',
        loadComponent: () =>
          import('./trading-hub/my-referrals/my-referrals.component').then(
            (m) => m.MyReferralsPageComponent,
          ),
      },
    ],
  },
];
